/**
 * Default payload factories for each contract type.
 */

export const defaultFixedPayload = () => ({
  peak_value: 0,
  mid_peak_value: 0,
  off_peak_value: 0,
  super_off_peak_value: 0,
  multiplierPMD: 1,
  multiplierERSE: 1,
});

export const defaultKoefficientPayload = () => ({
  k1_peak_value: 0,
  k1_mid_peak_value: 0,
  k1_off_peak_value: 0,
  k1_super_off_peak_value: 0,
  k2_peak_value: 0,
  k2_mid_peak_value: 0,
  k2_off_peak_value: 0,
  k2_super_off_peak_value: 0,
  k5_peak_value: 0,
  k5_mid_peak_value: 0,
  k5_off_peak_value: 0,
  k5_super_off_peak_value: 0,
  k6_peak_value: 0,
  k6_mid_peak_value: 0,
  k6_off_peak_value: 0,
  k6_super_off_peak_value: 0,
});

export const defaultIndexedSimplifiedPayload = () => ({
  multiplierPMD: 1,
  multiplierERSE: 1,
  additiveC: 0,
});

/**
 * Returns the default payload for a given contract type.
 */
export const getDefaultPayload = (type) => {
  switch (type) {
    case "NO_VALUES":
      return null;
    case "FIXED":
      return defaultFixedPayload();
    case "INDEXED":
      return defaultKoefficientPayload();
    case "INDEXED_SIMPLIFIED":
      return defaultIndexedSimplifiedPayload();
    default:
      return null;
  }
};

/**
 * Extracts the payload from a contract response, stripping backend-only fields.
 */
export const extractPayload = (payload, type) => {
  if (type === "NO_VALUES") return null;
  if (!payload) return getDefaultPayload(type);

  if (type === "FIXED") {
    return {
      peak_value: payload.peak_value ?? 0,
      mid_peak_value: payload.mid_peak_value ?? 0,
      off_peak_value: payload.off_peak_value ?? 0,
      super_off_peak_value: payload.super_off_peak_value ?? 0,
      multiplierPMD: payload.multiplierPMD ?? 1,
      multiplierERSE: payload.multiplierERSE ?? 1,
    };
  }

  if (type === "INDEXED") {
    const result = {};
    ["k1", "k2", "k5", "k6"].forEach((k) => {
      ["peak_value", "mid_peak_value", "off_peak_value", "super_off_peak_value"].forEach(
        (period) => {
          const key = `${k}_${period}`;
          result[key] = payload[key] ?? 0;
        }
      );
    });
    return result;
  }

  if (type === "INDEXED_SIMPLIFIED") {
    return {
      multiplierPMD: payload.multiplierPMD ?? 1,
      multiplierERSE: payload.multiplierERSE ?? 1,
      additiveC: payload.additiveC ?? 0,
    };
  }

  return getDefaultPayload(type);
};

/**
 * Deep-compare two plain objects (shallow keys, primitive values).
 */
export const payloadsAreEqual = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every((key) => a[key] === b[key]);
};

export default {
  defaultFixedPayload,
  defaultKoefficientPayload,
  defaultIndexedSimplifiedPayload,
  getDefaultPayload,
  extractPayload,
  payloadsAreEqual,
};
